import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContactButton from "../components/FloatingContactButton";
import ServicesModal, { shouldShowModal } from "../components/ServicesModal";
import ServiceSlideshow from "../components/ServiceSlideshow";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../components/SEO";

const SodInstallation = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState({
    header: false,
    content: false,
    cta: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sod installation images for slideshow
  const sodInstallationImages = [
    "/grass_replace_after1.jpeg",
    "/sod_installation_2.jpg",
    "/sod_installation_3.jpg"
  ];

  useEffect(() => {
    const handleScroll = () => {
      const headerSection = document.getElementById("header-section");
      const contentSection = document.getElementById("content-section");
      const ctaSection = document.getElementById("cta-section");

      if (headerSection) {
        setIsVisible((prev) => ({ ...prev, header: true }));
      }

      if (
        contentSection &&
        window.scrollY > contentSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, content: true }));
      }

      if (
        ctaSection &&
        window.scrollY > ctaSection.offsetTop - window.innerHeight * 0.8
      ) {
        setIsVisible((prev) => ({ ...prev, cta: true }));
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-show modal after 3 seconds (only if not shown before in this session)
  useEffect(() => {
    if (shouldShowModal()) {
      const timer = setTimeout(() => {
        setIsModalOpen(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const features = [
    {
      title: t("sodInstallation.features.quality.title"),
      description: t("sodInstallation.features.quality.description"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-primary-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: t("sodInstallation.features.preparation.title"),
      description: t("sodInstallation.features.preparation.description"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-primary-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      title: t("sodInstallation.features.installation.title"),
      description: t("sodInstallation.features.installation.description"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-primary-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M4 6h16M4 10h16M4 14h16M4 18h16"
          />
        </svg>
      ),
    },
    {
      title: t("sodInstallation.features.watering.title"),
      description: t("sodInstallation.features.watering.description"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-primary-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      ),
    },
    {
      title: t("sodInstallation.features.aftercare.title"),
      description: t("sodInstallation.features.aftercare.description"),
      icon: (
        <img
          src="/growing_tree.svg"
          alt="Post-Installation Care"
          className="h-10 w-10"
          style={{ filter: 'brightness(0) saturate(100%) invert(71%) sepia(66%) saturate(5985%) hue-rotate(127deg) brightness(100%) contrast(87%)' }}
        />
      ),
    },
    {
      title: t("sodInstallation.features.warranty.title"),
      description: t("sodInstallation.features.warranty.description"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-primary-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      ),
    },
  ];

  const benefits = (() => {
    const result = t("sodInstallation.benefits", {
      returnObjects: true,
    });
    return Array.isArray(result) ? result : [];
  })();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={t("sodInstallation.title") + " | Bearz Landscaping"}
        description={t("sodInstallation.subtitle")}
        pathname="/services/sod-installation"
      />
      <Navbar />

      {/* Header Section */}
      <section
        id="header-section"
        className={`relative pt-32 pb-16 sm:pt-36 sm:pb-24 bg-primary-900 transition-opacity duration-1000 ease-in-out ${
          isVisible.header ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 animate-fadeInUp">
            {t("sodInstallation.title")}
          </h1>
          <p className="text-lg sm:text-xl text-primary-100 mb-8 max-w-3xl mx-auto animate-fadeInUp animation-delay-200">
            {t("sodInstallation.subtitle")}
          </p>
          <div className="w-24 h-1.5 bg-primary-500 mx-auto rounded-full mb-8"></div>
        </div>
      </section>

      {/* Main Content Section */}
      <section
        id="content-section"
        className={`py-16 sm:py-24 bg-white transition-all duration-1000 ease-in-out ${
          isVisible.content
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Service Overview */}
          <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 items-center mb-16 sm:mb-24">
            <div className="w-full lg:w-1/2">
              <ServiceSlideshow
                images={sodInstallationImages}
                alt={t("sodInstallation.title")}
                className="w-full h-72 sm:h-96 object-cover rounded-xl shadow-lg"
              />
            </div>
            <div className="w-full lg:w-1/2 space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900">
                {t("sodInstallation.overviewTitle")}
              </h2>
              <p className="text-lg text-neutral-600">
                {t("sodInstallation.overviewDescription")}
              </p>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <svg
                      className="h-6 w-6 text-primary-500 mr-3 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-neutral-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Middle CTA Section */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 mb-16 sm:mb-24 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              {t("sodInstallation.ctaMiddle.title")}
            </h3>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              {t("sodInstallation.ctaMiddle.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/contact"
                className="inline-flex items-center bg-white text-primary-600 font-semibold rounded-full px-8 py-3 hover:bg-neutral-100 transition-all duration-300 transform hover:scale-105"
              >
                {t("sodInstallation.ctaMiddle.getQuote")}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </Link>
              <a
                href="tel:514-979-8020"
                className="inline-flex items-center border border-white text-white hover:bg-white hover:text-primary-600 font-semibold rounded-full px-6 py-3 transition-all duration-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                {t("sodInstallation.ctaMiddle.callNow")}
              </a>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-16 sm:mb-24">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                {t("sodInstallation.processTitle")}
              </h2>
              <div className="w-20 sm:w-24 h-1.5 bg-primary-500 mx-auto rounded-full"></div>
              <p className="mt-4 sm:mt-6 text-base sm:text-xl text-neutral-600 max-w-3xl mx-auto">
                {t("sodInstallation.processDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-neutral-50 rounded-xl p-6 sm:p-8 border border-neutral-200 hover:border-primary-200 transition-colors duration-300"
                >
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-4 text-neutral-900">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process Steps */}
          <div className="bg-neutral-50 rounded-xl p-8 sm:p-12 mb-16 sm:mb-24">
            <h3 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-6 text-center">
              {t("sodInstallation.processTitle")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary-600 font-bold text-xl">1</span>
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">
                  {t("sodInstallation.process.step1.title")}
                </h4>
                <p className="text-neutral-600">
                  {t("sodInstallation.process.step1.description")}
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary-600 font-bold text-xl">2</span>
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">
                  {t("sodInstallation.process.step2.title")}
                </h4>
                <p className="text-neutral-600">
                  {t("sodInstallation.process.step2.description")}
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary-600 font-bold text-xl">3</span>
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">
                  {t("sodInstallation.process.step3.title")}
                </h4>
                <p className="text-neutral-600">
                  {t("sodInstallation.process.step3.description")}
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary-600 font-bold text-xl">4</span>
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">
                  {t("sodInstallation.process.step4.title")}
                </h4>
                <p className="text-neutral-600">
                  {t("sodInstallation.process.step4.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="cta-section"
        className={`relative py-16 sm:py-20 bg-primary-800 transition-all duration-1000 ease-in-out ${
          isVisible.cta
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform translate-y-10"
        }`}
      >
        <div className="absolute inset-0 bg-primary-800 opacity-75"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            {t("sodInstallation.cta.title")}
          </h2>
          <p className="text-base sm:text-xl text-primary-100 mb-6 sm:mb-10 max-w-3xl mx-auto">
            {t("sodInstallation.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-white hover:bg-neutral-100 text-primary-800 font-semibold rounded-full px-8 py-3 sm:py-4 text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {t("sodInstallation.cta.getQuote")}
            </Link>
            <a
              href="tel:514-979-8020"
              className="flex items-center text-white hover:text-primary-200 transition-colors duration-300 mt-4 sm:mt-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              514-979-8020
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButton />
      <ServicesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default SodInstallation;
